import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { User } from '@supabase/supabase-js';
import {
  Organization,
  OrganizationMember,
  MemberRole,
  Invitation,
  ROLE_PERMISSIONS,
  isSubscriptionActive,
  canCreateProject,
  canAddMember,
  canCreateOrganization,
} from '../types/tenant';
import {
  getUserOrganizations,
  getUserDefaultOrganization,
  switchOrganization as switchOrgService,
  createOrganization,
  updateOrganization,
  getOrganizationMembers,
  getOrganizationMember,
  getUserPendingInvitations,
  getUserOwnedOrganizations,
} from '../services/organizationService';
import {
  getOrganizationProjects,
  getMonthlyProjectCreationCount,
} from '../services/projectService';

// =============================================================================
// TYPES
// =============================================================================

interface OrganizationContextValue {
  currentOrg: Organization | null;
  userOrgs: Organization[];
  userRole: MemberRole | null;
  members: OrganizationMember[];
  pendingInvitations: Invitation[];

  loading: boolean;
  orgLoading: boolean;
  needsOnboarding: boolean;

  isOwner: boolean;
  isAdmin: boolean;
  canManageMembers: boolean;
  canManageBilling: boolean;
  canCreateProjects: boolean;
  canEditProjects: boolean;
  hasActiveSubscription: boolean;
  canCreateOrg: boolean;
  monthlyProjectCount: number;
  memberCount: number;

  switchOrg: (orgId: string) => Promise<void>;
  refreshOrgs: (force?: boolean) => Promise<void>;
  refreshMembers: () => Promise<void>;
  createNewOrg: (name: string) => Promise<Organization>;
  updateOrgName: (name: string) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface OrganizationProviderProps {
  user: User | null;
  children: ReactNode;
}

export function OrganizationProvider({ user, children }: OrganizationProviderProps) {
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [userOrgs, setUserOrgs] = useState<Organization[]>([]);
  const [ownedOrgs, setOwnedOrgs] = useState<Organization[]>([]);
  const [userRole, setUserRole] = useState<MemberRole | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [monthlyProjectCount, setMonthlyProjectCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [orgLoading, setOrgLoading] = useState<boolean>(false);
  const loadedUserRef = React.useRef<string | null>(null);
  const loadingRef = React.useRef<boolean>(false);

  const refreshOrgs = useCallback(
    async (force = false) => {
      if (!user?.id) {
        setUserOrgs([]);
        setOwnedOrgs([]);
        setCurrentOrg(null);
        setUserRole(null);
        setLoading(false);
        return;
      }

      if (!force && loadedUserRef.current === user.id) return;
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoading(true);

        const [orgs, defaultOrg, owned] = await Promise.all([
          getUserOrganizations(user.id),
          getUserDefaultOrganization(user.id),
          getUserOwnedOrganizations(user.id),
        ]);

        setUserOrgs(orgs);
        setOwnedOrgs(owned);

        if (defaultOrg) {
          setCurrentOrg(defaultOrg);

          // Get member details and monthly project count in parallel
          const [member, orgMembers, monthlyCount] = await Promise.all([
            getOrganizationMember(defaultOrg.id, user.id),
            getOrganizationMembers(defaultOrg.id),
            getMonthlyProjectCreationCount(defaultOrg.id),
          ]);

          setUserRole(member?.role || null);
          setMembers(orgMembers);
          setMonthlyProjectCount(monthlyCount);
        } else if (orgs.length === 0) {
          console.warn('User has no organizations. They should create one.');
          setCurrentOrg(null);
          setUserRole(null);
        }

        // Get pending invitations
        if (user.email) {
          const invites = await getUserPendingInvitations(user.email);
          setPendingInvitations(invites);
        }

        loadedUserRef.current = user.id;
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [user?.id, user?.email]
  );

  // Refresh members list
  const refreshMembers = useCallback(async () => {
    if (!currentOrg) return;

    try {
      const orgMembers = await getOrganizationMembers(currentOrg.id);
      setMembers(orgMembers);
    } catch (err) {
      console.error('Failed to refresh members:', err);
    }
  }, [currentOrg]);

  // Switch to a different organization
  const switchOrg = useCallback(
    async (orgId: string) => {
      if (!user?.id) return;

      try {
        setOrgLoading(true);

        await switchOrgService(user.id, orgId);

        // Find the org in our list
        const targetOrg = userOrgs.find((org) => org.id === orgId);
        if (targetOrg) {
          setCurrentOrg(targetOrg);

          // Get user's role in this org
          const member = await getOrganizationMember(orgId, user.id);
          setUserRole(member?.role || null);

          // Get members and monthly project count in parallel
          const [orgMembers, monthlyCount] = await Promise.all([
            getOrganizationMembers(orgId),
            getMonthlyProjectCreationCount(orgId),
          ]);
          setMembers(orgMembers);
          setMonthlyProjectCount(monthlyCount);
        }
      } catch (err) {
        console.error('Failed to switch organization:', err);
        throw err;
      } finally {
        setOrgLoading(false);
      }
    },
    [user?.id, userOrgs]
  );

  const createNewOrg = useCallback(
    async (name: string): Promise<Organization> => {
      if (!user?.id) {
        throw new Error('User must be logged in to create an organization');
      }

      const newOrg = await createOrganization(user.id, { name });

      setUserOrgs((prev) => [...prev, newOrg]);
      setOwnedOrgs((prev) => [...prev, newOrg]);

      await switchOrg(newOrg.id);

      return newOrg;
    },
    [user?.id, switchOrg]
  );

  const updateOrgName = useCallback(
    async (name: string): Promise<void> => {
      if (!currentOrg) {
        throw new Error('No organization selected');
      }

      await updateOrganization(currentOrg.id, { name });

      setCurrentOrg((prev) => (prev ? { ...prev, name } : null));
      setUserOrgs((prev) => prev.map((org) => (org.id === currentOrg.id ? { ...org, name } : org)));
      setOwnedOrgs((prev) =>
        prev.map((org) => (org.id === currentOrg.id ? { ...org, name } : org))
      );
    },
    [currentOrg]
  );

  // Load on mount and when user changes
  useEffect(() => {
    refreshOrgs();
  }, [refreshOrgs]);

  const computed = useMemo(() => {
    const role = userRole;
    const permissions = role ? ROLE_PERMISSIONS[role] : null;

    return {
      isOwner: role === 'owner',
      isAdmin: role === 'owner' || role === 'admin',
      canManageMembers: permissions?.canManageMembers ?? false,
      canManageBilling: permissions?.canManageBilling ?? false,
      canCreateProjects: permissions?.canCreateProjects ?? false,
      canEditProjects: permissions?.canEditProjects ?? false,
      hasActiveSubscription: currentOrg ? isSubscriptionActive(currentOrg.subscription) : false,
      canCreateOrg: canCreateOrganization(ownedOrgs),
      needsOnboarding: !loading && userOrgs.length === 0,
    };
  }, [userRole, currentOrg, ownedOrgs, loading, userOrgs.length]);

  // Context value
  const value = useMemo<OrganizationContextValue>(
    () => ({
      currentOrg,
      userOrgs,
      userRole,
      members,
      pendingInvitations,
      loading,
      orgLoading,
      monthlyProjectCount,
      memberCount: members.length,
      ...computed,
      switchOrg,
      refreshOrgs,
      refreshMembers,
      createNewOrg,
      updateOrgName,
    }),
    [
      currentOrg,
      userOrgs,
      userRole,
      members,
      pendingInvitations,
      loading,
      orgLoading,
      monthlyProjectCount,
      computed,
      switchOrg,
      refreshOrgs,
      refreshMembers,
      createNewOrg,
      updateOrgName,
    ]
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);

  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }

  return context;
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Check if user has a specific permission in the current org.
 */
export function useOrgPermission(permission: keyof (typeof ROLE_PERMISSIONS)[MemberRole]): boolean {
  const { userRole } = useOrganization();

  if (!userRole) return false;
  return ROLE_PERMISSIONS[userRole][permission];
}

/**
 * Get the date when the monthly project quota resets (1st of next month UTC).
 */
function getMonthlyResetDate(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

/**
 * Check if user can perform an action based on subscription limits.
 * Project limits are now monthly (reset on 1st of each month UTC).
 */
export function useSubscriptionLimits() {
  const { currentOrg, monthlyProjectCount, memberCount } = useOrganization();

  return useMemo(() => {
    if (!currentOrg) {
      return {
        canAddProject: false,
        canAddMember: false,
        maxProjectsPerMonth: 0,
        maxMembers: 0,
        projectsCreatedThisMonth: 0,
        projectsRemainingThisMonth: 0,
        projectQuotaResetsAt: getMonthlyResetDate(),
        membersRemaining: 0,
      };
    }

    const sub = currentOrg.subscription;
    const maxProjects = sub.features.maxProjects;
    const maxMembers = sub.features.maxMembers;

    return {
      canAddProject: canCreateProject(sub, monthlyProjectCount),
      canAddMember: canAddMember(sub, memberCount),
      maxProjectsPerMonth: maxProjects === -1 ? Infinity : maxProjects,
      maxMembers: maxMembers === -1 ? Infinity : maxMembers,
      projectsCreatedThisMonth: monthlyProjectCount,
      projectsRemainingThisMonth:
        maxProjects === -1 ? Infinity : Math.max(0, maxProjects - monthlyProjectCount),
      projectQuotaResetsAt: getMonthlyResetDate(),
      membersRemaining: maxMembers === -1 ? Infinity : Math.max(0, maxMembers - memberCount),
    };
  }, [currentOrg, monthlyProjectCount, memberCount]);
}

/**
 * Get the current organization ID safely.
 */
export function useOrgId(): string | null {
  const { currentOrg } = useOrganization();
  return currentOrg?.id ?? null;
}
